import React, { useEffect, useState } from "react";
import LText from "@components/LText";
import LBox from "@components/LBox";
import LIcon from "@components/LIcon";
import { trackOnb } from "../analytics/trackOnboarding";
import { fonts, theme } from "@theme";
import { Platform } from "react-native";
import { useFocusEffect } from "expo-router";
import Animated, {
  useSharedValue,
  withTiming,
  withSpring,
  Easing,
  useAnimatedProps,
  useAnimatedStyle,
  useAnimatedReaction,
  runOnJS,
} from "react-native-reanimated";
import Svg, { Path, Defs, LinearGradient, Stop } from "react-native-svg";
import * as Haptics from "expo-haptics";
import { captureException } from "@utils/captureException";
import IntroLayout from "./IntroLayout";

const AnimatedPath = Animated.createAnimatedComponent(Path);

const CHECKLIST = [
  "Streaming Pattern Match",
  "Listener Profile Creation",
  "Royalties Calculation",
] as const;

// Semi-circle arc config
const ARC_SIZE = 200;
const STROKE_WIDTH = 12;
const RADIUS = (ARC_SIZE - STROKE_WIDTH) / 2;
// Arc spans 240 degrees (from 150° to 390°/30°), open at bottom
const ARC_DEGREES = 240;
const ARC_RADIANS = (ARC_DEGREES * Math.PI) / 180;
const ARC_LENGTH = ARC_RADIANS * RADIUS;

// Start angle: 150° (bottom-left), sweep 240° clockwise to 30° (bottom-right)
const START_ANGLE = (150 * Math.PI) / 180;
const END_ANGLE = ((150 + ARC_DEGREES) * Math.PI) / 180;

const CX = ARC_SIZE / 2;
const CY = ARC_SIZE / 2;

const arcStartX = CX + RADIUS * Math.cos(START_ANGLE);
const arcStartY = CY + RADIUS * Math.sin(START_ANGLE);
const arcEndX = CX + RADIUS * Math.cos(END_ANGLE);
const arcEndY = CY + RADIUS * Math.sin(END_ANGLE);

const BG_ARC_PATH = `M ${arcStartX} ${arcStartY} A ${RADIUS} ${RADIUS} 0 1 1 ${arcEndX} ${arcEndY}`;
const FG_ARC_PATH = BG_ARC_PATH;

const STEP_DURATION = 1080;
const TOTAL_DURATION = CHECKLIST.length * STEP_DURATION;

const ACCENT_DARK = "#2F80ED";
const ACCENT_LIGHT = "#56CCF2";
const CHECK_GREEN = "#4ADE80";

const DEV_DEBUG = false;

// Checklist item with tick animation
const ChecklistItem = ({
  label,
  isDone,
  isLast,
  checkIcon,
  waitingIcon,
  isNext,
}: {
  label: string;
  isDone: boolean;
  isLast: boolean;
  checkIcon: React.ReactNode;
  waitingIcon: React.ReactNode;
  isNext: boolean;
}) => {
  const scale = useSharedValue(isDone ? 1 : 0);
  const opacity = useSharedValue(isDone ? 1 : 0);
  const rotation = useSharedValue(0);

  useEffect(() => {
    if (isDone || !isNext) return;
    // Flip 180°, wait, flip 180°, wait — sandclock effect
    let cancelled = false;
    const flip = (from: number) => {
      if (cancelled) return;
      const to = from + 180;
      rotation.value = from;
      rotation.value = withTiming(to, {
        duration: 600,
        easing: Easing.inOut(Easing.cubic),
      });
      const timer = setTimeout(() => flip(to), 600 + 800);
      timerRef.current = timer;
    };
    const timerRef = { current: null as ReturnType<typeof setTimeout> | null };
    flip(0);
    return () => {
      cancelled = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [isDone, isNext, rotation]);

  const waitingStyle = useAnimatedStyle(() => ({
    transform: [{ rotate: `${rotation.value}deg` }],
  }));

  useEffect(() => {
    if (isDone) {
      scale.value = 0.3;
      opacity.value = 0;
      scale.value = withSpring(1, {
        damping: 20,
        stiffness: 300,
        overshootClamping: true,
      });
      opacity.value = withTiming(1, { duration: 200 });
    } else {
      scale.value = 0;
      opacity.value = 0;
    }
  }, [isDone, scale, opacity]);

  const checkStyle = useAnimatedStyle(() => ({
    transform: [{ scale: scale.value }],
    opacity: opacity.value,
  }));

  return (
    <>
      <LBox
        flexDirection="row"
        alignItems="center"
        justifyContent="space-between"
        paddingVertical="s4"
        paddingHorizontal="s4"
      >
        <LText
          fontSize={16}
          fontFamily={fonts.medium}
          color={isDone ? "white" : "whiteTranslucent60"}
        >
          {label}
        </LText>
        <LBox
          width={28}
          height={28}
          alignItems="center"
          justifyContent="center"
        >
          {isDone ? (
            <Animated.View style={checkStyle}>{checkIcon}</Animated.View>
          ) : (
            <Animated.View style={waitingStyle}>{waitingIcon}</Animated.View>
          )}
        </LBox>
      </LBox>
      {!isLast && (
        <LBox
          style={{
            height: 1,
            backgroundColor: "rgba(255,255,255,0.05)",
          }}
        />
      )}
    </>
  );
};

const AnalysingScreen = ({ onComplete }: { onComplete?: () => void }) => {
  const progress = useSharedValue(0);
  const [completedSteps, setCompletedSteps] = useState(0);
  const [showContinue, setShowContinue] = useState(false);

  useEffect(() => {
    trackOnb("signup", "analysing", "view");
    // completedSteps starts at 0, so no reset needed on mount.
    progress.set(0);
    progress.set(
      withTiming(1, {
        duration: TOTAL_DURATION,
        easing: Easing.out(Easing.quad),
      }),
    );
  }, [progress]);

  // If screen regains focus and animation is done, show continue button
  useFocusEffect(
    React.useCallback(() => {
      if (progress.value >= 0.99) {
        setShowContinue(true);
        setCompletedSteps(CHECKLIST.length);
      }
    }, [progress]),
  );

  const tickStep = React.useCallback((step: number) => {
    setCompletedSteps(step);
    if (Platform.OS !== "web") {
      Haptics.impactAsync(Haptics.ImpactFeedbackStyle.Medium).catch((error) => {
        captureException(error, {
          channel: "ignore",
          area: "haptics",
          operation: "analysing-step-impact",
        });
      });
    }
  }, []);

  const handleComplete = React.useCallback(() => {
    trackOnb("signup", "analysing", "completed");
    onComplete?.();
  }, [onComplete]);

  const TICK_THRESHOLDS = [0.3, 0.6, 0.9];

  useAnimatedReaction(
    () => progress.value,
    (val, prev) => {
      for (let i = 0; i < TICK_THRESHOLDS.length; i++) {
        if ((prev ?? 0) < TICK_THRESHOLDS[i] && val >= TICK_THRESHOLDS[i]) {
          runOnJS(tickStep)(i + 1);
        }
      }
      if (!DEV_DEBUG && (prev ?? 0) < 1 && val >= 0.99) {
        runOnJS(handleComplete)();
      }
    },
  );

  const animatedArcProps = useAnimatedProps(() => ({
    strokeDashoffset: ARC_LENGTH * (1 - progress.value),
  }));

  return (
    <IntroLayout
      totalSteps={0}
      currentStep={0}
      hideSteps
      buttonLabel={showContinue ? "Continue" : undefined}
      buttonEventName="analysing"
      onButtonPress={showContinue ? onComplete : undefined}
    >
      <LBox flex={1} alignItems="center">
        <LText
          fontSize={32}
          fontFamily={fonts.bold}
          lineHeight={38}
          textAlign="center"
          mb="l1"
          mt="l3"
        >
          {"Let\u2019s prepare\nyour report"}
        </LText>

        {/* Semi-circle progress */}
        <LBox
          alignItems="center"
          justifyContent="center"
          mt="l1"
          mb="s3"
          style={{ width: ARC_SIZE, height: ARC_SIZE }}
        >
          <Svg width={ARC_SIZE} height={ARC_SIZE}>
            <Defs>
              <LinearGradient id="arcGrad" x1="0" y1="0" x2="1" y2="0">
                <Stop offset="0" stopColor={ACCENT_DARK} />
                <Stop offset="1" stopColor={ACCENT_LIGHT} />
              </LinearGradient>
            </Defs>
            {/* Background arc */}
            <Path
              d={BG_ARC_PATH}
              stroke={theme.colors.greyBorder}
              strokeWidth={STROKE_WIDTH}
              fill="none"
              strokeLinecap="round"
              opacity={0.3}
            />
            {/* Foreground arc */}
            <AnimatedPath
              d={FG_ARC_PATH}
              stroke="url(#arcGrad)"
              strokeWidth={STROKE_WIDTH}
              fill="none"
              strokeDasharray={ARC_LENGTH}
              animatedProps={animatedArcProps}
              strokeLinecap="round"
            />
          </Svg>
          <Animated.View
            style={{
              position: "absolute",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <PercentText progress={progress} />
          </Animated.View>
        </LBox>

        {/* Loading subtitle */}
        <LBox mb="l2" style={{ height: 20 }}>
          {completedSteps < CHECKLIST.length && (
            <LText
              fontSize={15}
              fontFamily={fonts.regular}
              color="whiteTranslucent60"
            >
              Loading...
            </LText>
          )}
        </LBox>

        {/* Checklist card */}
        <LBox
          width="100%"
          borderRadius="l"
          style={{ backgroundColor: "#171B27" }}
          overflow="hidden"
        >
          {CHECKLIST.map((item, i) => (
            <ChecklistItem
              key={item}
              label={item}
              isDone={i < completedSteps}
              isNext={i === completedSteps}
              isLast={i === CHECKLIST.length - 1}
              checkIcon={
                <LText fontSize={20} style={{ color: CHECK_GREEN }}>
                  ✓
                </LText>
              }
              waitingIcon={
                <LIcon
                  colors={{ color: "whiteTranslucent60" }}
                  name="timer"
                  size={20}
                />
              }
            />
          ))}
        </LBox>
      </LBox>
    </IntroLayout>
  );
};

// Animated percentage text
const PercentText = ({ progress }: { progress: { value: number } }) => {
  const [percent, setPercent] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      const val = Math.round(progress.value * 100);
      setPercent(val);
    }, 50);
    return () => clearInterval(interval);
  }, [progress]);

  return (
    <LText
      fontSize={48}
      fontFamily={fonts.bold}
      style={{ color: ACCENT_LIGHT }}
      lineHeight={56}
    >
      {percent}%
    </LText>
  );
};

export default AnalysingScreen;
